/** Customer record shape & helpers for Organization Management (UI layer). */

export const emptyContact = () => ({
  id: `ct-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  name: "",
  email: "",
  mobile: "",
  role: "",
});

/** Portal admin sub-record (customer create API). */
export const emptyPortalAdmin = () => ({
  username: "",
  password: "",
  confirmPassword: "",
  firstName: "",
  lastName: "",
  mobile: "",
  countryCode: "+91",
});

export const customerInitial = () => ({
  customerId: "",
  customerCode: "",
  customerName: "",
  customerShortName: "",
  customerType: "Retailer",
  customerGroup: "",
  customerSegment: "",
  salesChannel: "",
  marketRegion: "",
  orgMappingId: "",
  parentCustomerId: "",
  billingName: "",
  billingAddressText: "",
  shippingAddressText: "",
  billAddress1: "",
  billAddress2: "",
  billCity: "",
  billState: "",
  billPostal: "",
  shipAddress1: "",
  shipAddress2: "",
  shipCity: "",
  shipState: "",
  shipPostal: "",
  taxRegistrationNumber: "",
  panRegistrationRef: "",
  orderCurrency: "INR",
  priceListId: "",
  portalLogin: true,
  portalUsernameEmail: "",
  portalUserRole: "",
  /** Module capability ids (same source as partner: SYS_CAP / `getGlobalOptions`). */
  moduleAccess: [],
  portalAdmin: emptyPortalAdmin(),
  salesOrderEligible: true,
  customerStatus: "Draft",
  customerEffectiveFrom: "",
  customerEffectiveTo: "",
  customerRemarks: "",
  primaryContactName: "",
  primaryContactEmail: "",
  primaryContactPhone: "",
  customerContacts: [emptyContact()],
  paymentTerms: "",
  creditLimit: "",
  pricingBasis: "Customer",
  salesOwner: "",
  defaultWarehouse: "",
  createdBy: "",
  createdOn: "",
  updatedBy: "",
  updatedOn: "",
});

/** Merge legacy line-based addresses into textarea fields for display. */
export function hydrateCustomerAddresses(c) {
  const next = { ...c };
  if (!next.billingAddressText?.trim()) {
    const parts = [next.billAddress1, next.billAddress2, [next.billCity, next.billState, next.billPostal].filter(Boolean).join(", ")].filter(Boolean);
    next.billingAddressText = parts.join("\n");
  }
  if (!next.shippingAddressText?.trim()) {
    const parts = [next.shipAddress1, next.shipAddress2, [next.shipCity, next.shipState, next.shipPostal].filter(Boolean).join(", ")].filter(Boolean);
    next.shippingAddressText = parts.join("\n");
  }
  return next;
}

/** Persist textarea back into structured fields (best-effort for exports/API). */
export function syncStructuredAddressesFromText(c) {
  const lines = (c.billingAddressText || "").split("\n").map((l) => l.trim());
  const ships = (c.shippingAddressText || "").split("\n").map((l) => l.trim());
  return {
    ...c,
    billAddress1: lines[0] || "",
    billAddress2: lines[1] || "",
    shipAddress1: ships[0] || "",
    shipAddress2: ships[1] || "",
  };
}

export function normalizeCustomerContacts(raw) {
  const list = raw?.length ? raw : [emptyContact()];
  return list.map((x) => ({
    ...emptyContact(),
    ...x,
    id: x.id || emptyContact().id,
  }));
}

/** Normalize single customer from legacy `embeddedCustomer`. */
export function normalizeCustomerRecord(raw, orgId) {
  const base = { ...customerInitial(), ...raw, orgMappingId: orgId || raw.orgMappingId || "" };
  base.customerContacts = normalizeCustomerContacts(base.customerContacts);
  base.moduleAccess = Array.isArray(base.moduleAccess) ? [...base.moduleAccess] : [];
  base.portalAdmin = { ...emptyPortalAdmin(), ...(typeof base.portalAdmin === "object" ? base.portalAdmin : {}) };
  return hydrateCustomerAddresses(base);
}

/** Customers for an org node: `customers[]` or legacy `embeddedCustomer`. */
export function getOrgCustomers(org) {
  if (org?.customers?.length) {
    return org.customers.map((c) => normalizeCustomerRecord(c, org.id));
  }
  if (org?.embeddedCustomer && (org.embeddedCustomer.customerCode || org.embeddedCustomer.customerName)) {
    return [normalizeCustomerRecord(org.embeddedCustomer, org.id)];
  }
  return [];
}

/** Ensure every org has `customers` array (migrates embedded). */
export function migrateOrgFlat(flat) {
  return flat.map((n) => {
    if (n.customers?.length) return { ...n, customers: n.customers.map((c) => normalizeCustomerRecord(c, n.id)) };
    const list = getOrgCustomers(n);
    return { ...n, customers: list.length ? list : [] };
  });
}
